// Boolean feature flag name. Extensible: add new values here as new flags are
// introduced (matches the BFF's `flagName` enum).
export type FeatureFlagName =
  | "MANAGER_LICENCE"
  | "DOCTOR_LICENCE"
  | "STATIC_DOCTOR_DIRECTORY";

export const FeatureFlagNameEnum = {
  MANAGER_LICENCE: "MANAGER_LICENCE",
  DOCTOR_LICENCE: "DOCTOR_LICENCE",
  STATIC_DOCTOR_DIRECTORY: "STATIC_DOCTOR_DIRECTORY",
} as const;

export const FEATURE_FLAG_NAMES: FeatureFlagName[] = [
  "MANAGER_LICENCE",
  "DOCTOR_LICENCE",
  "STATIC_DOCTOR_DIRECTORY",
];

// Supported environments/countries for FeatureFlag rules (same domain values as
// AppNotice rules, kept as an independent module on purpose).
export type FeatureFlagEnvironment = "local" | "dev" | "qa" | "prod";

export const FEATURE_FLAG_ENVIRONMENTS: FeatureFlagEnvironment[] = [
  "local",
  "dev",
  "qa",
  "prod",
];

export type FeatureFlagCountry = "senegal" | "tchad";

export const FEATURE_FLAG_COUNTRIES: FeatureFlagCountry[] = [
  "senegal",
  "tchad",
];

// DTO used inside the app (camelCase). `environment`/`country` are nullable —
// null means the rule applies globally.
export interface FeatureFlagDto {
  id: number;
  flagName: FeatureFlagName;
  environment: FeatureFlagEnvironment | null;
  country: FeatureFlagCountry | null;
  enabled: boolean;
  createdBy: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

// Wire shape returned by the BFF (snake_case, per platform convention).
export interface FeatureFlagWire {
  id: number;
  flagName: FeatureFlagName;
  environment: string | null;
  country: string | null;
  enabled: boolean;
  created_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

// Payload sent on create / update (snake_case to match the BFF contract).
export interface FeatureFlagPayload {
  flagName: FeatureFlagName;
  environment: string | null;
  country: string | null;
  enabled: boolean;
}

// VM used inside the form & redux state (camelCase).
export interface FeatureFlagInput {
  flagName: FeatureFlagName;
  environment: FeatureFlagEnvironment | null;
  country: FeatureFlagCountry | null;
  enabled: boolean;
}

// ── Mappers ───────────────────────────────────────────────────────────────

export function fromWire(wire: FeatureFlagWire): FeatureFlagDto {
  return {
    id: wire.id,
    flagName: wire.flagName,
    environment: (wire.environment as FeatureFlagEnvironment | null) ?? null,
    country: (wire.country as FeatureFlagCountry | null) ?? null,
    enabled: wire.enabled,
    createdBy: wire.created_by,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

export function toPayload(input: FeatureFlagInput): FeatureFlagPayload {
  return {
    flagName: input.flagName,
    environment: input.environment ?? null,
    country: input.country ?? null,
    enabled: input.enabled,
  };
}
